import { Button, ButtonGroup, Tooltip } from '@mairistem/composants';
import {
	type Mouvement,
	type ParametreGeneralEntite,
	Sens,
} from '@mairistem/comptabilite';
import { Combobox, Icon } from '@mairistem/finances-utils';
import { isNewIdentifiant } from '@mairistem/tools';
import React from 'react';
import { toast } from 'react-toastify';
import { GroupeProfilEcritureCode } from '../../../../constants/mouvements/groupesProfilEcriture';
import type ProfilEcriture from '../../../../interfaces/pieces/mouvements/profilEcriture';
import getProfilEcritures from '../../../../services/piecesComptables/profilEcritures/profilEcritures';
import {
	filterProfilsForMouvement,
	isProfilLibre,
} from '../../../../utils/mouvements/profilEcriture';
import ProfilEcritureModal from './ProfilEcritureModal';
import ProfilEcritureResetModal from './ProfilEcritureResetModal';

interface ProfilEcritureComboboxProps {
	codeGroupeProfilEcriture: string;
	codeProfil: string;
	entite: ParametreGeneralEntite;
	identifiant: number | string;
	isDirty: React.MutableRefObject<boolean>;
	mouvement: Mouvement;
	onChange: (codeProfilEcriture: string) => void;
	onClear: (codeProfilEcriture: string) => void;
	sens: string;
}

const PROFIL_LIBRE_DEPENSE = {
	code: 'DSL',
	identifiant: 0,
	libelle: 'Avec une saisie libre',
	sens: Sens.DEPENSE,
};

const PROFIL_LIBRE_RECETTE = {
	code: 'RSL',
	identifiant: 1,
	libelle: 'Avec une saisie libre',
	sens: Sens.RECETTE,
};

const ProfilEcritureCombobox = ({
	codeGroupeProfilEcriture,
	codeProfil,
	identifiant,
	isDirty,
	mouvement,
	onChange,
	onClear,
}: ProfilEcritureComboboxProps) => {
	const [allProfils, setAllProfils] = React.useState<ProfilEcriture[]>([]);
	const [codeProfilSelected, setCodeProfilSelected] = React.useState<string>();
	const [openFrequentModal, setOpenFrequentModal] = React.useState(false);
	const [openResetModal, setOpenResetModal] = React.useState(false);

	// Chargement de la liste des profils d'écriture
	React.useEffect(() => {
		if (codeGroupeProfilEcriture && mouvement?.sens) {
			const fetchProfilsEcriture = async () => {
				const searchParams = new URLSearchParams({
					'groupes[]': codeGroupeProfilEcriture,
					all: 'true',
					sens: mouvement.sens,
				});

				try {
					const { data } = await getProfilEcritures(searchParams);

					setAllProfils(data);
				} catch {
					toast.error(
						"Impossible de récupérer la liste des profils d'écriture",
					);
				}
			};

			fetchProfilsEcriture();
		}
	}, [codeGroupeProfilEcriture, mouvement?.sens]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: On veut mouvement?.bordereau?.typePes
	const renderOptions = React.useMemo(() => {
		let profils: ProfilEcriture[] = [];

		if (allProfils.length > 0 && mouvement?.sens) {
			if (isNewIdentifiant(identifiant)) {
				profils = filterProfilsForMouvement(allProfils, mouvement);
			} else {
				// Le profil actuel de la piece
				const actualProfilOnMouvement = allProfils.find(
					(profil) => profil.code === codeProfil,
				);

				profils = [
					// Condition pour ne pas avoir deux fois la saisie libre
					...(isProfilLibre(codeProfil) ? [] : [actualProfilOnMouvement]),
					// On a le droit que de choisir les profils libres si on est deja sur une piece existante
					...(codeGroupeProfilEcriture === GroupeProfilEcritureCode.OPO
						? []
						: [
								mouvement?.sens === Sens.DEPENSE
									? PROFIL_LIBRE_DEPENSE
									: PROFIL_LIBRE_RECETTE,
							]),
				];
			}
		}

		return profils.map((profil) => ({
			key: profil.identifiant,
			text: profil.libelle,
			value: profil.code,
		}));
	}, [allProfils, identifiant, mouvement?.bordereau?.typePes]);

	const handleCancel = () => {
		setOpenResetModal(false);
	};

	const handleChange = (value: string) => {
		if (isNewIdentifiant(identifiant)) {
			// Si on est actuellement (codeProfil) sur un profil autre que libre et que le profil (code) choisi n'est pas libre
			if (!isProfilLibre(codeProfil) && !isProfilLibre(value)) {
				// Si on a fait des modifications sur la page
				if (isDirty.current) {
					setCodeProfilSelected(value);
					// On affiche la modal de reset
					setOpenResetModal(true);
				} else {
					onChange(value);
				}
			} else {
				onChange(value);
			}
		} else if (isProfilLibre(value)) {
			onChange(value);
		}
	};

	const handleReset = () => {
		onClear(codeProfilSelected);
		setOpenResetModal(false);
	};

	const handleSelect = (value: string) => {
		handleChange(value);
		setOpenFrequentModal(false);
	};

	return (
		<>
			<ButtonGroup compact fluid>
				<Combobox
					data-testid="profilEcritureCombobox"
					disabled={!isNewIdentifiant(identifiant) && isProfilLibre(codeProfil)}
					fluid
					noResultsMessage="Aucun profil d'écriture trouvé"
					onChange={(_e, { value }) => handleChange(value as string)}
					options={renderOptions}
					placeholder="Rechercher un profil d'écriture"
					search
					selection
					value={codeProfil}
				/>
				{isNewIdentifiant(identifiant) && (
					<Tooltip
						icon="info"
						message="Choisir parmi les profils fréquemment utilisés"
						trigger={
							<Button onClick={() => setOpenFrequentModal(true)}>
								<Icon iconSet="Lucide" name="History" size={18} />
							</Button>
						}
						type="information"
					/>
				)}
			</ButtonGroup>
			{openFrequentModal && (
				<ProfilEcritureModal
					codeGroupeProfilEcriture={codeGroupeProfilEcriture}
					identifiantEntite={mouvement?.exercice?.identifiantEntite}
					mouvement={mouvement}
					onClose={() => setOpenFrequentModal(false)}
					onSelect={handleSelect}
				/>
			)}
			<ProfilEcritureResetModal
				onCancel={handleCancel}
				onReset={handleReset}
				open={openResetModal}
			/>
		</>
	);
};

export default ProfilEcritureCombobox;
